import {Box, SelectChangeEvent} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  IQueryFieldOperator,
  QBOperators,
  getFileTransferListOperators,
} from '../../../../utils/queryBuilder';
import AurDuelTransferList, {
  KeyValue,
} from '../../../common/aur-duel-transfer-list/AurDuelTransferList';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  FieldUIType,
  FieldUITypeMappingObj,
  IFieldModal,
} from '../../field-management/custom-field/constant';
import {QueryFieldDropdown} from './components/QueryFieldDropdown';
import {useQueryFileField} from './useQueryFileField';

export interface IQueryFieldFileTransferListParams {
  operator?: QBOperators;
  value?: string;
}

interface IQueryFieldFileTransferListProps {
  selectedField?: IFieldModal;
  initialInputValue?: IQueryFieldFileTransferListParams;
  onChangeFieldParams?: (params: IQueryFieldFileTransferListParams) => void;
}

const QueryFieldFileTransferList: React.FC<
  IQueryFieldFileTransferListProps
> = ({selectedField, initialInputValue, onChangeFieldParams}) => {
  const operatorArray: IQueryFieldOperator[] = getFileTransferListOperators(
    FieldUITypeMappingObj[(selectedField?.fieldTypeId || 1) as FieldUIType]
  );

  const {value: initialValue = '*'} = initialInputValue ?? {};

  const [selectedOperator, setSelectedOperator] = useState<QBOperators>(
    initialInputValue?.operator ?? operatorArray[0].name
  );

  const [fieldValue, setFieldValue] = useState<string[]>(
    initialValue === '*' ? [] : initialValue.split(' OR ')
  );

  const updateFieldValue = useCallback(
    (updatedFieldValue: KeyValue[]) => {
      const areArraysDifferent =
        fieldValue?.length !== updatedFieldValue.length ||
        fieldValue.some((val, i) => val !== updatedFieldValue[i].key);

      if (areArraysDifferent) {
        setFieldValue(updatedFieldValue.map((item) => item.key));
      }
    },
    [fieldValue]
  );

  const {loading, transferListKeys} = useQueryFileField(
    selectedField?.fieldId ?? 0
  );

  const transferListKeyValues: KeyValue[] = useMemo(
    () =>
      (transferListKeys ?? []).map((item) => {
        return {
          key: item,
          label: item,
        };
      }),
    [transferListKeys]
  );

  const selectedFieldKeyValues: KeyValue[] = useMemo(
    () =>
      fieldValue.map((item) => {
        return {
          key: item,
          label: item,
        };
      }),
    [fieldValue]
  );

  const isTransferListOperator = useMemo(
    () =>
      ![
        QBOperators.isSet,
        QBOperators.isNotSet,
        QBOperators.isSetText,
        QBOperators.isNotSetText,
      ].includes(selectedOperator),
    [selectedOperator]
  );

  useEffect(() => {
    onChangeFieldParams?.({
      operator: selectedOperator,
      value: isTransferListOperator ? fieldValue.join(' OR ') : '*',
    });
  }, [
    fieldValue,
    isTransferListOperator,
    onChangeFieldParams,
    selectedOperator,
  ]);

  const handleConditionChange = useCallback(
    (event: SelectChangeEvent) => {
      const name = operatorArray.find(
        (op) => op.label === event.target.value
      )?.name;

      setSelectedOperator(name as QBOperators);
    },
    [operatorArray]
  );

  const renderTransferList = useCallback(() => {
    return (
      <Box className={'transfer-list-container'}>
        <AurDuelTransferList
          isDisabled={!isTransferListOperator}
          availableFieldsOrignal={transferListKeyValues}
          selectedFieldsOrignal={
            !isTransferListOperator ? [] : selectedFieldKeyValues
          }
          onSelectedFieldsChange={updateFieldValue}
        />
      </Box>
    );
  }, [
    isTransferListOperator,
    selectedFieldKeyValues,
    transferListKeyValues,
    updateFieldValue,
  ]);

  // use loading here
  if (loading) {
    return (
      <Box className={'file-transfer-list-field-loading-container'}>
        <AurLoadingIndicator />
      </Box>
    );
  }

  return (
    <Box className={'file-transfer-list-field-parent-container'}>
      <QueryFieldDropdown
        fieldLabel={selectedField?.fieldLabel}
        selectedOperator={selectedOperator}
        operators={operatorArray}
        handleConditionChange={handleConditionChange}
      />
      {renderTransferList()}
    </Box>
  );
};

export {QueryFieldFileTransferList};
